import React from 'react';
import Leaflet, {LatLngLiteral} from 'leaflet';
import {Marker, Tooltip, useMap} from 'react-leaflet';

import {useRecordContext} from 'components/record/useRecordContext';
import {Check, Place, Timer, TimerOff, Visibility, VisibilityOff} from '@mui/icons-material';
import {BooleanField, ListField} from 'components/list/Fields';
import ActionMenu from 'components/list/actionMenu/ActionMenu';
import ActionMenuItemUpdate from 'components/list/actionMenu/ActionMenuItemUpdate';
import ActionMenuItemDelete from 'components/list/actionMenu/ActionMenuItemDelete';
import List from 'components/list/List';
import {ResourceContextProvider} from 'components/record/ResourceContext';
import CreateButton from 'components/forms/CreateButton';
import ControlForm from './ControlForm';
import ActionMenuItemEdit from 'components/list/actionMenu/ActionMenuItemEdit';
import ActionMenuItemMergeControl from './ActionMenuItemMergeControl';
import BulkDelete from 'components/forms/BulkDelete';
import BulkUpdate from 'components/forms/BulkUpdate';
import {Box, Card, CardContent, Stack} from '@mui/material';
import {useFetch} from 'hooks/useFetchData';
import {EventType} from 'types/App';
import EventMapContainer from './EventMapContainer';
import icons, {IconType} from './Icons/MarkerIcons';

const isIconType = (type?: string): type is IconType =>
  type === 'default' || type === 'center' || type === 'circle' || type === 'square' || type === 'triangle';

type ControlPin = {
  id: number;
  name: string;
  type?: string;
  position?: LatLngLiteral | null;
};

const controlIcon = (type?: string) => icons[isIconType(type) ? type : 'default'];

const FitControlBounds = ({positions}: {positions: LatLngLiteral[]}) => {
  const map = useMap();

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
      if (positions.length === 1) {
        map.setView(positions[0], Math.max(map.getZoom(), 14));
        return;
      }
      if (positions.length > 1) {
        map.fitBounds(Leaflet.latLngBounds(positions), {padding: [28, 28], maxZoom: 16});
      }
    }, 150);
    return () => window.clearTimeout(timer);
  }, [map, positions]);

  return null;
};

const ControlsMap = ({event}: {event: EventType}) => {
  const {data} = useFetch<{items: ControlPin[]}>('GET_LIST', 'Control', {
    variables: {slug: event.slug, filters: {}}
  });
  const controls = React.useMemo(() => data?.items || [], [data?.items]);
  const positions = React.useMemo(
    () => controls.map((control) => control.position).filter((position): position is LatLngLiteral => Boolean(position?.lat && position?.lng)),
    [controls]
  );

  return (
    <Box sx={{height: {xs: 280, sm: 420}, borderRadius: 1, overflow: 'hidden'}}>
      <EventMapContainer event={event} style={{width: '100%', height: '100%', position: 'relative'}}>
        <FitControlBounds positions={positions} />
        {controls.map(
          (control) =>
            control.position && (
              <Marker key={control.id} position={control.position} icon={controlIcon(control.type)} draggable={false}>
                <Tooltip>{control.name}</Tooltip>
              </Marker>
            )
        )}
      </EventMapContainer>
    </Box>
  );
};

const ControlsTable = () => {
  const {record: event} = useRecordContext();

  return (
    <Stack spacing={2}>
      <ResourceContextProvider resource="Control" params={{slug: event.slug}}>
        <Card>
          <CardContent sx={{p: 1.5, '&:last-child': {pb: 1.5}}}>
            <ControlsMap event={event} />
          </CardContent>
        </Card>
        <List
          bulkOptions
          hidePagination
          title="Controls"
          avatar={<Place />}
          toolbar={
            <React.Fragment>
              <CreateButton label="Add New Control">
                <ControlForm event={event} />
              </CreateButton>
              <CreateButton label="Add Many Controls" many>
                <ControlForm event={event} multiControls />
              </CreateButton>
            </React.Fragment>
          }
          bulkActions={
            <Box gap={2} display="flex">
              <BulkUpdate data={{show: true}} label="Show" icon={<Visibility />} />
              <BulkUpdate data={{show: false}} label="Hide" icon={<VisibilityOff />} />
              <BulkUpdate data={{analytic: true}} label="Add analytic" icon={<Timer />} />
              <BulkUpdate data={{analytic: false}} label="Hide analytic" icon={<TimerOff />} />
              <BulkDelete />
            </Box>
          }
          isOrderable
        >
          <ListField source="name" label="Name" />
          <ListField source="type" label="Type" />
          {event.settings?.useSI && <ListField source="siCode" label="SI" />}
          <BooleanField source="show" label="Show">
            <Check />
          </BooleanField>
          <BooleanField source="analytic" label="Analytic">
            <Check />
          </BooleanField>
          <ActionMenu label="Actions" align="right" width="5%" variant="cell">
            <ActionMenuItemEdit confirmContent={<ControlForm event={event} />} />
            <ActionMenuItemMergeControl />
            <ActionMenuItemUpdate label="Show" icon={<Visibility />} data={{show: true}} />
            <ActionMenuItemUpdate label="Hide" icon={<VisibilityOff />} data={{show: false}} />
            <ActionMenuItemDelete />
          </ActionMenu>
        </List>
      </ResourceContextProvider>
    </Stack>
  );
};

export default ControlsTable;
